"use client";

import { useCallback } from "react";
import type {
  ConfirmThoughtsRequest,
  DeleteThoughtsRequest,
  MeiliThought,
  MindstringPrototype,
} from "@/generated/fastapi";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "@/shared/feedback";
import useConfirmThoughtsMutation from "../mutations/useConfirmThoughtsMutation";
import useDeleteThoughtsMutation from "../mutations/useDeleteThoughtsMutation";
import useThoughtRefresh from "./useThoughtRefresh";

export default function useThoughtActions() {
  const deleteThoughtsMutation = useDeleteThoughtsMutation();
  const confirmThoughtsMutation = useConfirmThoughtsMutation();
  const {
    refreshReflectionSurfaces,
    syncConfirmedThoughtMutation,
    syncDeletedThoughts,
  } = useThoughtRefresh();

  const handleThoughtsDeleteClick = useCallback(
    async (
      request: DeleteThoughtsRequest,
      onSuccess?: () => void,
      affectedPrototypes?: MindstringPrototype[],
    ) => {
      try {
        const response = await deleteThoughtsMutation.mutateAsync(request);
        if (response.status !== "success" && response.status !== "pending") {
          showErrorToast(response.message || "Failed to delete thoughts");
          return;
        }

        syncDeletedThoughts({
          response,
          indexName: request.sync_meili_index,
          prototypes: affectedPrototypes,
        });
        onSuccess?.();

        if (response.status === "pending") {
          showWarningToast(
            response.message ||
              "Thoughts removed, but search is still catching up.",
          );
        } else {
          showSuccessToast("Thoughts deleted successfully");
        }
      } catch {
        showErrorToast("Failed to delete thoughts");
      }
    },
    [deleteThoughtsMutation, syncDeletedThoughts],
  );

  const handleThoughtUpdateClick = useCallback(
    async ({
      request,
      updatedThought,
      onSuccess,
      affectedPrototypes,
    }: {
      request: ConfirmThoughtsRequest;
      updatedThought: MeiliThought;
      onSuccess?: (updatedThought: MeiliThought) => void;
      affectedPrototypes?: MindstringPrototype[];
    }) => {
      try {
        const response = await confirmThoughtsMutation.mutateAsync(request);

        if (response.status === "success" || response.status === "pending") {
          onSuccess?.(updatedThought);

          if (request.reset_reflections) {
            refreshReflectionSurfaces();
          }

          syncConfirmedThoughtMutation(affectedPrototypes);

          if (response.status === "pending") {
            showWarningToast(
              response.message ||
                "Thought updated, but search is still catching up.",
            );
          } else {
            showSuccessToast("Thought updated successfully");
          }
          return;
        }

        if (response.status === "not_found") {
          showErrorToast(`Not successful: ${response.message}`);
          return;
        }

        if (response.status === "validation_error") {
          showErrorToast(response.message || "Failed to update thought");
          return;
        }

        showErrorToast(response.message || "Failed to update thought");
      } catch {
        showErrorToast("Failed to update thought");
      }
    },
    [
      confirmThoughtsMutation,
      refreshReflectionSurfaces,
      syncConfirmedThoughtMutation,
    ],
  );

  return {
    handleThoughtsDeleteClick,
    handleThoughtUpdateClick,
  };
}
